import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TICKET_PRIORITY_ORDER,
  ticketPriorityLabelKey,
  type SlaPolicy,
  type TicketPriority,
} from '@nbh/shared';
import { adminApi } from '../../lib/adminApi';

/**
 * The property's SLA windows, editable at last (D8, Q-CONFIG-NOW).
 *
 * Eight numbers, all in minutes. The store keeps resolve targets in hours because
 * that is the schema's unit, but the operator thinks in minutes — the D-SEEDS
 * table is written in minutes — so the screen converts at the edge, both ways.
 *
 * Root-only, like every tab that can change what the property has promised. The
 * server refuses anyone else regardless; the tab bar merely does not offer it.
 */

/** Per-priority minutes, as edited. Strings, so a half-typed number is not a NaN. */
type Draft = Record<TicketPriority, { ack: string; resolve: string }>;

function toDraft(policy: SlaPolicy): Draft {
  return Object.fromEntries(
    TICKET_PRIORITY_ORDER.map((priority) => [
      priority,
      {
        ack: String(policy[priority].ackTimeoutMinutes),
        resolve: String(policy[priority].resolveTimeoutHours * 60),
      },
    ]),
  ) as Draft;
}

/** Null when any field is empty or not a positive number — the save stays disabled. */
function toPolicy(draft: Draft): SlaPolicy | null {
  const entries = TICKET_PRIORITY_ORDER.map((priority) => {
    const ack = Number(draft[priority].ack);
    const resolve = Number(draft[priority].resolve);
    if (!Number.isFinite(ack) || ack <= 0 || !Number.isFinite(resolve) || resolve <= 0) {
      return null;
    }
    return [priority, { ackTimeoutMinutes: ack, resolveTimeoutHours: resolve / 60 }] as const;
  });
  if (entries.some((entry) => entry === null)) return null;

  return Object.fromEntries(entries as NonNullable<(typeof entries)[number]>[]) as SlaPolicy;
}

export function SlaPolicyPanel() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [source, setSource] = useState<'stored' | 'default'>('stored');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.superAdmin.slaPolicy
      .get()
      .then((view) => {
        setDraft(toDraft(view.policy));
        setSource(view.source);
      })
      .catch((cause: Error) => setError(cause.message));
  }, []);

  const policy = draft ? toPolicy(draft) : null;

  async function save() {
    if (!policy) return;
    setStatus('saving');
    setError(null);
    try {
      const view = await adminApi.superAdmin.slaPolicy.set(policy);
      setDraft(toDraft(view.policy));
      setSource(view.source);
      setStatus('saved');
    } catch (cause) {
      setStatus('idle');
      setError((cause as Error).message);
    }
  }

  function edit(priority: TicketPriority, field: 'ack' | 'resolve', value: string) {
    setStatus('idle');
    setDraft((previous) =>
      previous ? { ...previous, [priority]: { ...previous[priority], [field]: value } } : previous,
    );
  }

  if (!draft) {
    return error ? (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    ) : (
      <p className="text-sm text-slate-400">{t('admin.loading')}</p>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-100">{t('superAdmin.sla.title')}</h2>
        <p className="mt-1 text-sm text-slate-400">{t('superAdmin.sla.intro')}</p>
        {source === 'default' && (
          <p className="mt-1 text-sm text-amber-400">{t('superAdmin.sla.defaultNote')}</p>
        )}
      </div>

      <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
        {TICKET_PRIORITY_ORDER.map((priority) => (
          <div key={priority} className="flex flex-wrap items-center gap-3 p-3">
            <span className="w-20 text-sm font-medium text-slate-200">
              {t(ticketPriorityLabelKey[priority])}
            </span>
            <label className="flex flex-1 items-center gap-2 text-sm text-slate-400">
              {t('superAdmin.sla.ack')}
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={draft[priority].ack}
                onChange={(event) => edit(priority, 'ack', event.target.value)}
                className="min-h-[48px] w-20 rounded-lg border border-slate-700 bg-slate-900 px-2 text-right text-sm text-slate-100"
              />
            </label>
            <label className="flex flex-1 items-center gap-2 text-sm text-slate-400">
              {t('superAdmin.sla.resolve')}
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={draft[priority].resolve}
                onChange={(event) => edit(priority, 'resolve', event.target.value)}
                className="min-h-[48px] w-20 rounded-lg border border-slate-700 bg-slate-900 px-2 text-right text-sm text-slate-100"
              />
            </label>
          </div>
        ))}
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!policy || status === 'saving'}
          className="min-h-[48px] rounded-lg bg-brookfields-gold px-6 text-sm font-semibold text-slate-950 disabled:opacity-50"
        >
          {t('superAdmin.sla.save')}
        </button>
        {status === 'saved' && (
          <span role="status" className="text-sm text-emerald-400">
            {t('superAdmin.sla.saved')}
          </span>
        )}
      </div>
    </div>
  );
}
